import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import ThemeToggleButton from "../helper/ThemeToggleButton";

const MasterLayout = ({ children }) => {
  let [sidebarActive, seSidebarActive] = useState(false);
  let [mobileMenu, setMobileMenu] = useState(false);
  const location = useLocation(); // Hook to get the current route
  const navigate = useNavigate(); // Hook for navigation
  const [user, setUser] = useState({ fullname: '', role: '', username: '' });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [pageAccess, setPageAccess] = useState({}); // Store page access permissions
  const [isLoadingAccess, setIsLoadingAccess] = useState(true);

  // Check authentication on component mount and whenever route changes
  useEffect(() => {
    const checkAuthentication = () => {
      const token = localStorage.getItem('authToken');
      const storedUser = localStorage.getItem('user');

      // If no token or user data, redirect to login
      if (!token || !storedUser) {
        localStorage.clear(); // Clear any remaining data
        navigate('/sign-in', { replace: true });
        setIsCheckingAuth(false);
        return;
      }

      try {
        // Validate user data
        const userData = JSON.parse(storedUser);
        
        if (!userData.id || !userData.username) {
          throw new Error('Invalid user data');
        }

        // Set user data
        const userRole = (userData.role || 'user').toLowerCase(); // Normalize to lowercase
        setUser({
          fullname: userData.fullname || 'User',
          role: userRole,
          username: userData.username || ''
        });
        
        // Fetch dynamic page access permissions
        fetchPageAccess(userRole, token);
        
        setIsAuthenticated(true);
        setIsCheckingAuth(false);
      } catch (error) {
        console.error('Error validating authentication:', error);
        localStorage.clear();
        navigate('/sign-in', { replace: true });
        setIsCheckingAuth(false);
      }
    };

    checkAuthentication();
  }, [navigate, location.pathname]);

  // Fetch page access permissions
  const fetchPageAccess = async (role, token) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/role-page-access/my-access`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Create a map of page_route -> access info
          const accessMap = {};
          data.data.forEach(access => {
            accessMap[access.page_route] = {
              can_access: access.can_access,
              show_in_menu: access.show_in_menu,
              menu_order: access.menu_order,
              menu_label: access.menu_label,
            };
          });
          setPageAccess(accessMap);
        }
      }
    } catch (error) {
      console.error('Failed to fetch page access:', error);
      // Fallback to default behavior if API fails
    } finally {
      setIsLoadingAccess(false);
    }
  };

  // Check if user can access a route
  const canAccessRoute = (route) => {
    if (isLoadingAccess) return true; // Allow access while loading to prevent redirect loops
    const access = pageAccess[route];
    // If route exists in permissions, check can_access
    // If route doesn't exist in permissions, deny access (security: explicit permission required)
    return access ? access.can_access : false;
  };

  // Check if menu item should be shown
  const shouldShowInMenu = (route) => {
    if (isLoadingAccess) return false; // Wait for permissions to load
    const access = pageAccess[route];
    return access ? access.show_in_menu : false;
  };

  // Check route access on route change
  useEffect(() => {
    // Only check route access if:
    // 1. Not loading permissions
    // 2. User is authenticated
    // 3. We have loaded permissions (pageAccess has at least one entry OR we've finished loading)
    if (!isLoadingAccess && isAuthenticated && Object.keys(pageAccess).length > 0) {
      const currentRoute = location.pathname;
      const hasAccess = canAccessRoute(currentRoute);
      
      if (!hasAccess && currentRoute !== '/sign-in') {
        // Redirect to dashboard if no access
        navigate('/', { replace: true });
      }
    }
  }, [location.pathname, pageAccess, isLoadingAccess, isAuthenticated, navigate]);

  // Toggle dropdown function
  const toggleDropdown = (dropdownName) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  let sidebarControl = () => {
    seSidebarActive(!sidebarActive);
  };

  let mobileMenuControl = () => {
    setMobileMenu(!mobileMenu);
  };

  // Logout handler
  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      // Call logout API to invalidate token on server
      if (token) {
        try {
          await fetch(`${process.env.REACT_APP_API_URL}/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });
        } catch (apiError) {
          console.error('API logout error:', apiError);
          // Continue with local logout even if API call fails
        }
      }
      
      // Clear all authentication data from localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('role');
      
      // Redirect to login page
      navigate('/sign-in');
    } catch (error) {
      console.error('Error during logout:', error);
      // Still redirect even if there's an error
      navigate('/sign-in');
    }
  };

  // Show loading state while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary-600" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-secondary-light">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Don't render the layout if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <section className={mobileMenu ? "overlay active" : "overlay "}>
      {/* sidebar */}
      <aside
        className={
          sidebarActive
            ? "sidebar active "
            : mobileMenu
            ? "sidebar sidebar-open"
            : "sidebar"
        }
      >
        <button
          onClick={mobileMenuControl}
          type='button'
          className='sidebar-close-btn'
        >
          <Icon icon='radix-icons:cross-2' />
        </button>
        <div>
          <Link to='/' className='sidebar-logo'>
            <img
              src='assets/images/sidebarlogo.png'
              alt='site logo'
              className='light-logo'
            />
            {/* <img
              src='assets/images/sidebarlogo.png'
              alt='site logo'
              className='dark-logo'
            />
            <img
              src='assets/images/sidebarlogo.png'
              alt='site logo'
              className='logo-icon'
            /> */}
          </Link>
        </div>
        <div className='sidebar-menu-area'>
          <ul className='sidebar-menu' id='sidebar-menu'>
            {/* Dashboard - Dynamic visibility based on permissions */}
            {shouldShowInMenu('/') && (
              <li>
                <Link to='/'>
                  <Icon
                    icon='solar:home-smile-angle-outline'
                    className='menu-icon'
                  />
                  <span>Asset Dashboard</span>
                </Link>
              </li>
            )}
            
            {/* IT Asset Dashboard - Dynamic visibility */}
            {shouldShowInMenu('/it-asset-dashboard') && (
              <li>
                <Link to='/it-asset-dashboard'>
                  <Icon
                    icon='solar:home-smile-angle-outline'
                    className='menu-icon'
                  />
                  <span>IT Asset Dashboard</span>
                </Link>
              </li>
            )}

            {/* Rental Dashboard - Dynamic visibility */}
            {shouldShowInMenu('/rental-dashboard') && (
              <li>
                <Link to='/rental-dashboard'>
                  <Icon
                    icon='mdi:chart-box-outline'
                    className='menu-icon'
                  />
                  <span>Rental Dashboard</span>
                </Link>
              </li>
            )}

             {/* Roles Menu - Only visible for username "admin" */}
             {user.username === 'admin' && user.role === 'admin' && (
               <li>
                 <Link to='/roles'>
                   <Icon
                     icon='mdi:account-group'
                     className='menu-icon'
                   />
                   <span>Roles</span>
                 </Link>
               </li>
             )}

             {/* Users Dropdown - Dynamic visibility */}
             {(shouldShowInMenu('/users-list') || shouldShowInMenu('/add-user')) && (
             <li className={`dropdown ${openDropdown === 'users' ? 'open' : ''}`}>
              <a 
                href='#' 
                onClick={(e) => { 
                  e.preventDefault(); 
                  toggleDropdown('users'); 
                }}
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  cursor: 'pointer'
                }}
              >
                <Icon
                  icon='flowbite:users-group-outline'
                  className='menu-icon'
                />
                <span style={{ flex: 1, opacity: 1, visibility: 'visible' }}>Users</span>
              </a>
              <ul 
                className='sidebar-submenu' 
                style={{ 
                  display: openDropdown === 'users' ? 'block' : 'none',
                  maxHeight: openDropdown === 'users' ? '500px' : '0',
                  overflow: openDropdown === 'users' ? 'visible' : 'hidden',
                  transition: 'all 0.3s ease'
                }}
              >
                {shouldShowInMenu('/users-list') && (
                <li>
                  <NavLink
                    to='/users-list'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                    onClick={() => setOpenDropdown(null)}
                  >
                    <i className='ri-circle-fill circle-icon text-primary-600 w-auto' />{" "}
                    Users List
                  </NavLink>
                </li>
                )}
                {shouldShowInMenu('/add-user') && (
                <li>
                  <NavLink
                    to='/add-user'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                    onClick={() => setOpenDropdown(null)}
                  >
                    <i className='ri-circle-fill circle-icon text-info-main w-auto' />{" "}
                    Add User
                  </NavLink>
                </li>
                )}
              </ul>
            </li>
             )}

             {/* Assets Dropdown - Dynamic visibility */}
             {(shouldShowInMenu('/asset-list') || shouldShowInMenu('/add-asset')) && (
             <li className={`dropdown ${openDropdown === 'assets' ? 'open' : ''}`}>
              <a 
                href='#' 
                onClick={(e) => { 
                  e.preventDefault(); 
                  toggleDropdown('assets'); 
                }}
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  cursor: 'pointer'
                }}
              >
                <Icon
                  icon='solar:box-linear'
                  className='menu-icon'
                />
                <span style={{ flex: 1, opacity: 1, visibility: 'visible' }}>Assets</span>
              </a>
              <ul 
                className='sidebar-submenu' 
                style={{ 
                  display: openDropdown === 'assets' ? 'block' : 'none',
                  maxHeight: openDropdown === 'assets' ? '500px' : '0',
                  overflow: openDropdown === 'assets' ? 'visible' : 'hidden',
                  transition: 'all 0.3s ease'
                }}
              >
                {shouldShowInMenu('/asset-list') && (
                <li>
                  <NavLink
                    to='/asset-list'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                    onClick={() => setOpenDropdown(null)}
                  >
                    <i className='ri-circle-fill circle-icon text-primary-600 w-auto' />{" "}
                    Asset List
                  </NavLink>
                </li>
                )}
                {shouldShowInMenu('/add-asset') && (
                <li>
                  <NavLink
                    to='/add-asset'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                    onClick={() => setOpenDropdown(null)}
                  >
                    <i className='ri-circle-fill circle-icon text-info-main w-auto' />{" "}
                    Add Asset
                  </NavLink>
                </li>
                )}
              </ul>
            </li>
             )}

            {/* IT Asset Dropdown - Dynamic visibility */}
            {(shouldShowInMenu('/register-it-asset') || shouldShowInMenu('/it-asset-list')) && (
            <li className={`dropdown ${openDropdown === 'it-asset' ? 'open' : ''}`}>
              <a 
                href='#' 
                onClick={(e) => { 
                  e.preventDefault(); 
                  toggleDropdown('it-asset'); 
                }}
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  cursor: 'pointer'
                }}
              >
                <Icon
                  icon='mdi:laptop'
                  className='menu-icon'
                />
                <span style={{ flex: 1, opacity: 1, visibility: 'visible' }}>IT Asset</span>
              </a>
              <ul 
                className='sidebar-submenu' 
                style={{ 
                  display: openDropdown === 'it-asset' ? 'block' : 'none',
                  maxHeight: openDropdown === 'it-asset' ? '500px' : '0',
                  overflow: openDropdown === 'it-asset' ? 'visible' : 'hidden',
                  transition: 'all 0.3s ease'
                }}
              >
                {shouldShowInMenu('/register-it-asset') && (
                <li>
                  <NavLink
                    to='/register-it-asset'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                    onClick={() => setOpenDropdown(null)}
                  >
                    <i className='ri-circle-fill circle-icon text-primary-600 w-auto' />{" "}
                    Add IT Asset
                  </NavLink>
                </li>
                )}
                {shouldShowInMenu('/it-asset-list') && (
                <li>
                  <NavLink
                    to='/it-asset-list'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                    onClick={() => setOpenDropdown(null)}
                  >
                    <i className='ri-circle-fill circle-icon text-info-main w-auto' />{" "}
                    IT Asset List
                  </NavLink>
                </li>
                )}
              </ul>
            </li>
            )}

            {/* Rental Dropdown - Dynamic visibility */}
            {(shouldShowInMenu('/register-rental') || shouldShowInMenu('/register-monthly')) && (
            <li className={`dropdown ${openDropdown === 'rental' ? 'open' : ''}`}>
              <a 
                href='#' 
                onClick={(e) => { 
                  e.preventDefault(); 
                  toggleDropdown('rental'); 
                }}
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  cursor: 'pointer'
                }}
              >
                <Icon
                  icon='mdi:cash-register'
                  className='menu-icon'
                />
                <span style={{ flex: 1, opacity: 1, visibility: 'visible' }}>Rental</span>
              </a>
              <ul 
                className='sidebar-submenu' 
                style={{ 
                  display: openDropdown === 'rental' ? 'block' : 'none',
                  maxHeight: openDropdown === 'rental' ? '500px' : '0',
                  overflow: openDropdown === 'rental' ? 'visible' : 'hidden',
                  transition: 'all 0.3s ease'
                }}
              >
                {shouldShowInMenu('/register-rental') && (
                <li>
                  <NavLink
                    to='/register-rental'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                    onClick={() => setOpenDropdown(null)}
                  >
                    <i className='ri-circle-fill circle-icon text-primary-600 w-auto' />{" "}
                    Register Rental
                  </NavLink>
                </li>
                )}
                {shouldShowInMenu('/register-monthly') && (
                <li>
                  <NavLink
                    to='/register-monthly'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                    onClick={() => setOpenDropdown(null)}
                  >
                    <i className='ri-circle-fill circle-icon text-info-main w-auto' />{" "}
                    Register Monthly
                  </NavLink>
                </li>
                )}
              </ul>
            </li>
            )}

            {/* Report - Only visible for Admin */}
            {/* Report Asset - Dynamic visibility */}
            {shouldShowInMenu('/report') && (
              <li>
                <Link to='/report'>
                  <Icon
                    icon='mdi:file-chart'
                    className='menu-icon'
                  />
                  <span>Report Asset</span>
                </Link>
              </li>
            )}
            
            {/* IT Asset Report - Dynamic visibility */}
            {shouldShowInMenu('/it-asset-report') && (
              <li>
                <Link to='/it-asset-report'>
                  <Icon
                    icon='mdi:file-chart'
                    className='menu-icon'
                  />
                  <span>Report IT Asset</span>
                </Link>
              </li>
            )}
            
            {/* Report Rental - Dynamic visibility */}
            {shouldShowInMenu('/rental-report') && (
              <li>
                <Link to='/rental-report'>
                  <Icon
                    icon='mdi:file-chart'
                    className='menu-icon'
                  />
                  <span>Report Rental</span>
                </Link>
              </li>
            )}

            {/* Role Page Access Management - Only visible for username "admin" */}
            {user.username === 'admin' && user.role === 'admin' && (
              <li>
                <Link to='/role-page-access'>
                  <Icon
                    icon='mdi:shield-account'
                    className='menu-icon'
                  />
                  <span>Role Page Access</span>
                </Link>
              </li>
            )}

          </ul>
        </div>
      </aside>

      <main
        className={sidebarActive ? "dashboard-main active" : "dashboard-main"}
      >
        <div className='navbar-header'>
          <div className='row align-items-center justify-content-between'>
            <div className='col-auto'>
              <div className='d-flex flex-wrap align-items-center gap-4'>
                <button
                  type='button'
                  className='sidebar-toggle'
                  onClick={sidebarControl}
                >
                  {sidebarActive ? (
                    <Icon
                      icon='iconoir:arrow-right'
                      className='icon text-2xl non-active'
                    />
                  ) : (
                    <Icon
                      icon='heroicons:bars-3-solid'
                      className='icon text-2xl non-active '
                    />
                  )}
                </button>
                <button
                  onClick={mobileMenuControl}
                  type='button'
                  className='sidebar-mobile-toggle'
                >
                  <Icon icon='heroicons:bars-3-solid' className='icon' />
                </button>
                {/* <form className='navbar-search'>
                  <input type='text' name='search' placeholder='Search' />
                  <Icon icon='ion:search-outline' className='icon' />
                </form> */}
              </div>
            </div>
            <div className='col-auto'>
              <div className='d-flex flex-wrap align-items-center gap-3'>
                {/* ThemeToggleButton */}
                {/* <ThemeToggleButton /> */}
               
                
            
                <div className='dropdown'>
                  <button
                    className='d-flex justify-content-center align-items-center rounded-circle'
                    type='button'
                    data-bs-toggle='dropdown'
                  >
                    <img
                      src='assets/images/user.jpg'
                      alt='image_user'
                      className='w-40-px h-40-px object-fit-cover rounded-circle'
                    />
                  </button>
                  <div className='dropdown-menu to-top dropdown-menu-sm'>
                    <div className='py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2'>
                      <div>
                        <h6 className='text-lg text-primary-light fw-semibold mb-2'>
                          {user.fullname || 'User'}
                        </h6>
                        <span className='text-secondary-light fw-medium text-sm'>
                          {user.role || 'User'}
                        </span>
                      </div>
                      <button type='button' className='hover-text-danger'>
                        <Icon
                          icon='radix-icons:cross-1'
                          className='icon text-xl'
                        />
                      </button>
                    </div>
                    <ul className='to-top-list'>
                      <li>
                        <button
                          className='dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-danger d-flex align-items-center gap-3'
                          onClick={handleLogout}
                          style={{ border: 'none', background: 'transparent', width: '100%', cursor: 'pointer' }}
                        >
                          <Icon icon='lucide:power' className='icon text-xl' />{" "}
                          Log Out
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
                {/* Profile dropdown end */}
              </div>
            </div>
          </div>
        </div>

        {/* dashboard-main-body */}
        <div className='dashboard-main-body'>{children}</div>

        {/* Footer section */}
        <footer className='d-footer'>
          <div className='row align-items-center'>
            <div className='col-auto'>
              <p className='mb-0'>© {new Date().getFullYear()} SDRB IT Department. All Rights Reserved.</p>
            </div>
          </div>
        </footer>
      </main>
    </section>
  );
};

export default MasterLayout;
